import request from 'superagent';
import * as Immutable from 'immutable';
import ModelFactory from '../models/ModelFactory';
import NotImplementedException from '../exceptions/NotImplementedException';
import BrowserStorage from './BrowserStorage';
import AccessQueue from './AccessQueue';

export default class Client {
  static getEndpoint() {
    throw new NotImplementedException('getEndpoint must be implemented');
  }

  static getModelName() {
    throw new NotImplementedException('convert must be implemented');
  }

  static convert(json, modelName = null) {
    return ModelFactory.create(modelName || this.getModelName(), json);
  }

  static getHash(functionName, param1 = null, param2 = null) {
    return `${this.getEndpoint()}_${functionName}_${JSON.stringify(param1)}_${JSON.stringify(param2)}`;
  }

  static setCache(key, obj) {
    BrowserStorage.setItem(this.getEndpoint(), key, JSON.stringify(obj));
  }

  static getCache(key) {
    if (BrowserStorage.getItem(key)) {
      return JSON.parse(BrowserStorage.getItem(key));
    }
    return false;
  }

  static clearCache() {
    BrowserStorage.removeTagItems(this.getEndpoint());
  }

  static _get(id, params, storeCache = false) {
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.get(`${endpoint}/${id}`, params)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            if (storeCache) {
              this.setCache(this.getHash('get', id, params), res.body);
            }
            resolve(this.convert(res.body));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static get(id, params, storeCache = false) {
    return AccessQueue.getQueue().add(() => this._get(id, params, storeCache));
  }

  static getUseCache(id, params, refreshCache = true) {
    const hash = this.getHash('get', id, params);
    if (this.getCache(hash)) {
      if (refreshCache) {
        return AccessQueue.getCacheQueue().add(() => this._get(id, params, true));
      }
      return new Promise((resolve) => {
        resolve(this.convert(this.getCache(hash)));
      });
    }
    return this.get(id, params, true);
  }

  static create(model, params) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      const properties = model.getProperties();
      request.post(endpoint, Object.assign(properties, params))
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(this.convert(res.body));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static update(model, params) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      const properties = model.getProperties();
      request.patch(`${endpoint}/${model.id}`, Object.assign(properties, params))
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(this.convert(res.body));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static updateById(id, params) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.patch(`${endpoint}/${id}`, params)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(this.convert(res.body));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static delete(model) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.delete(`${endpoint}/${model.id}`)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(null);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static restore(model) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.patch(`${endpoint}/${model.id}/restore`)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(null);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static _getAll(params, storeCache = false) {
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.get(endpoint, params)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            if (storeCache) {
              this.setCache(this.getHash('getAll', params), res.body.models);
            }
            resolve(this.convertMultiple(res.body.models));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static getAll(params) {
    return AccessQueue.getQueue().add(() => this._getAll(params));
  }

  static getAllUseCache(params, refreshCache = true) {
    const hash = this.getHash('getAll', params);
    if (this.getCache(hash)) {
      if (refreshCache) {
        AccessQueue.getCacheQueue().add(() => this._getAll(params, true));
      }
      return new Promise((resolve) => {
        resolve(this.convertMultiple(this.getCache(hash)));
      });
    }
    return this.getAll(params, true);
  }

  static updateAll(models, params) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      const data = models.map((model) => model.getProperties());
      const requestParams = { ...params };
      requestParams.models = data;
      request.patch(endpoint, requestParams)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(this.convertMultiple(res.body.models));
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static convertMultiple(data, modelName = null) {
    return Immutable.Seq(data)
      .map((json) => this.convert(json, modelName))
      .toArray();
  }

  static handleErrors(res, err, reject) {
    console.error(err);
    const errorNormalized = err;
    if (!('body' in errorNormalized)) {
      errorNormalized.body = window.app.config.globalProperties.$t('common.failed');
    }
    switch (res?.status) {
      case 401:
        window.location.href = '/login';
        break;
      case 422:
        reject(Object.assign(errorNormalized, { body: res.body }));
        break;
      default:
        reject(errorNormalized);
    }
  }

  static catchErrors(error) {
    const promise = new Promise();
    Client.handleErrors(error?.res, error, promise.reject);
    return promise;
  }

  static moveAfter(model, positionModel) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.post(`${endpoint}/${model.id}/moveAfter/${positionModel.id}`)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(null);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static moveBefore(model, positionModel) {
    this.clearCache();
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.post(`${endpoint}/${model.id}/moveBefore/${positionModel.id}`)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(null);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static getResourceSelectedItems(params) {
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.get(`${endpoint}/get-selected-items`, params)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(res.body.models);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }

  static getDetail(id) {
    const endpoint = this.getEndpoint();
    return new Promise((resolve, reject) => {
      request.get(`${endpoint}/${id}`)
        .accept('json')
        .end((err, res) => {
          if (res?.ok) {
            resolve(res.body);
          } else {
            this.handleErrors(res, err, reject);
          }
        });
    });
  }
}
